import * as Sentry from "@sentry/react";
import { AxiosError } from "axios";

import { captureException } from "@/lib/errors";
import { posthog } from "@/lib/posthog";

const MAX_RESPONSE_SNIPPET_LENGTH = 500;

export type ErrorType =
  | "network_error"
  | "timeout"
  | "security_filter_blocked"
  | "http_403"
  | "http_4xx"
  | "http_5xx"
  | "unknown";

export interface RequestMetadata {
  url?: string;
  method?: string;
  params?: Record<string, unknown>;
}

export interface ErrorContext {
  operation: string;
  page: string;
}

export interface CapturedError {
  errorId: string;
  errorType: ErrorType;
  statusCode?: number;
  userMessage: string;
  timestamp: string;
  request?: RequestMetadata;
  responseSnippet?: string;
}

export const generateErrorId = (): string => crypto.randomUUID().slice(0, 8).toUpperCase();

const isAxiosError = (error: unknown): error is AxiosError =>
  error !== null &&
  typeof error === "object" &&
  "isAxiosError" in error &&
  (error as AxiosError).isAxiosError === true;

const isSecurityFilterBlocked = (error: unknown): boolean => {
  if (!isAxiosError(error) || !error.response) {
    return false;
  }

  const response = error.response;
  const status = response.status;

  if (status !== 403) {
    return false;
  }

  const contentType = response.headers?.["content-type"] || "";
  const isJsonResponse = contentType.toLowerCase().includes("application/json");

  if (isJsonResponse) {
    return false;
  }

  if (contentType.toLowerCase().includes("text/html")) {
    return true;
  }

  const data = response.data;
  if (typeof data === "string") {
    const trimmedData = data.trim().toLowerCase();
    if (trimmedData.startsWith("<!doctype") || trimmedData.startsWith("<html")) {
      return true;
    }
  }

  return false;
};

const classifyStatusCode = (status: number): { type: ErrorType; statusCode?: number } => {
  if (status === 0) return { type: "network_error" };
  if (status === 403) return { type: "http_403", statusCode: status };
  if (status >= 400 && status < 500) return { type: "http_4xx", statusCode: status };
  if (status >= 500) return { type: "http_5xx", statusCode: status };
  return { type: "unknown", statusCode: status };
};

export const classifyError = (error: unknown): { type: ErrorType; statusCode?: number } => {
  if (error === null || error === undefined) {
    return { type: "network_error" };
  }

  if (isAxiosError(error)) {
    if (isSecurityFilterBlocked(error)) {
      return { type: "security_filter_blocked", statusCode: 403 };
    }
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      return { type: "timeout" };
    }
    if (!error.response || typeof error.response.status !== "number") {
      return { type: "network_error" };
    }
    return classifyStatusCode(error.response.status);
  }

  if (typeof error === "object" && "response" in error) {
    const response = (error as { response?: { status?: number } }).response;
    if (response && typeof response.status === "number") {
      return classifyStatusCode(response.status);
    }
  }

  return { type: "network_error" };
};

export const isUnexpectedError = (error: unknown): boolean => {
  const { type } = classifyError(error);
  return (
    type === "network_error" ||
    type === "timeout" ||
    type === "security_filter_blocked" ||
    type === "http_403" ||
    type === "http_5xx"
  );
};

export const getUserMessage = (errorType: ErrorType, fallbackMessage?: string): string => {
  switch (errorType) {
    case "security_filter_blocked":
      return "This step was blocked by automated security filtering.";
    case "http_403":
      return "Access denied. You may not have permission to perform this action, or your session may have expired. Please try again or contact support.";
    case "network_error":
      return "Unable to connect to the server. Please check your internet connection and try again.";
    case "timeout":
      return "The request took too long to complete. Please check your connection and try again.";
    case "http_5xx":
      return "We're experiencing technical difficulties. Please try again in a few minutes.";
    case "http_4xx":
      return fallbackMessage || "The request could not be completed. Please try again.";
    default:
      return fallbackMessage || "An unexpected error occurred. Please try again or contact support.";
  }
};

const extractRequestMetadata = (error: unknown): RequestMetadata | undefined => {
  if (isAxiosError(error) && error.config) {
    return {
      url: error.config.url,
      method: error.config.method?.toUpperCase(),
    };
  }
  return undefined;
};

const extractResponseData = (error: unknown): string | undefined => {
  if (isAxiosError(error) && error.response?.data) {
    try {
      const data = error.response.data;
      if (typeof data === "string") {
        return data.slice(0, MAX_RESPONSE_SNIPPET_LENGTH);
      }
      return JSON.stringify(data).slice(0, MAX_RESPONSE_SNIPPET_LENGTH);
    } catch {
      return undefined;
    }
  }
  return undefined;
};

export const captureError = (
  error: unknown,
  context: ErrorContext,
  eventName: string = "client_error",
): CapturedError => {
  const { type: errorType, statusCode } = classifyError(error);
  const timestamp = new Date().toISOString();
  const fallbackMessage = error instanceof Error ? error.message : undefined;
  const userMessage = getUserMessage(errorType, fallbackMessage);
  const request = extractRequestMetadata(error);
  const responseSnippet = extractResponseData(error);

  Sentry.addBreadcrumb({
    category: "error.captured",
    message: `${errorType} during ${context.operation}`,
    level: "error",
    data: {
      page: context.page,
      ...(request && { url: request.url, method: request.method }),
    },
  });

  // Use Sentry's event ID so ops can search for the same ID in Sentry
  const { sentryEventId } = captureException(error, {
    tags: {
      error_type: errorType,
      operation: context.operation,
      page: context.page,
      ...(statusCode && { status_code: String(statusCode) }),
    },
    extra: {
      request_url: request?.url,
      request_method: request?.method,
      response_snippet: responseSnippet,
      user_message: userMessage,
    },
    fingerprint: ["{{ default }}", errorType, context.operation],
  });

  const errorId = sentryEventId || generateErrorId();

  posthog.capture(eventName, {
    error_id: errorId,
    error_type: errorType,
    operation: context.operation,
    page: context.page,
    status_code: statusCode,
    request_url: request?.url,
    request_method: request?.method,
  });

  return {
    errorId,
    errorType,
    statusCode,
    userMessage,
    timestamp,
    request,
    responseSnippet,
  };
};
